import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";
import NotificationModal from "../components/NotificationModal";
import { v4 as uuidv4 } from "uuid"; // Import uuid
import { uploadToStorage, compressImageToWebP } from "../utils/uploadToStorage";
import ImageCropperModal from "../components/ImageCropperModal";
import { Camera, Check, CheckCircle, ChevronLeft, Contact, UploadCloud } from "lucide-react";
import {
  validateCPF,
  validatePhone,
  validateCEP,
  validateDate,
  validateMinAge,
  validateURL,
  validateName,
} from "../utils/validators";
import {
  BABYSITTER_BASIC_SERVICES,
  BABYSITTER_EXTRA_SERVICES,
  sanitizeExtraServices,
} from "../shared/babysitterServices";
// CadastroBabysitter.css migrado para Tailwind

// =========================================================================
// FUNÇÕES DE UTILIDADE (MÁSCARAS)
// =========================================================================
const maskCPF = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

const maskCEP = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{3})\d+?$/, "$1");
};

const maskPhone = (value) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");
};

const newCertificateItem = () => ({
  id: uuidv4(),
  nome: "",
  file: null,
  previewUrl: null,
  fileError: null,
});
const diasSemana = [
  { key: "domingo", label: "Dom" },
  { key: "segunda", label: "Seg" },
  { key: "terca", label: "Ter" },
  { key: "quarta", label: "Qua" },
  { key: "quinta", label: "Qui" },
  { key: "sexta", label: "Sex" },
  { key: "sabado", label: "Sáb" },
];

const periodosTrabalho = [
  { key: "manha", label: "Manhã" },
  { key: "tarde", label: "Tarde" },
  { key: "noite", label: "Noite" },
  { key: "pernoite", label: "Pernoite" },
];

export default function CadastroBabysitter({
  adminEditingUserId,
  hideHeader = false,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState(null);
  const [stepError, setStepError] = useState("");
  const [stepWarn, setStepWarn] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "info",
  });

  const [dados, setDados] = useState({
    nome: "",
    sobrenome: "",
    dataNascimento: "",
    cpf: "",
    cep: "",
    cidade: "",
    endereco: "",
    estado: "",
    bairro: "",
    numero: "",
    complemento: "",
    telefone: "",
    experiencia: "",
    qualificacoes: "",
    idiomas: [],
    idiomasOutro: "", // Inicializado para evitar erro de componente controlado
    servicos: [],
    banco: "",
    agencia: "",
    conta: "",
    pix: "",
    redeSocial: "",
    nivelExperiencia: "",
    aceitaEmergencial: false,
    raio: 10,
    referencia1: { nome: "", contato: "" },
    referencia2: { nome: "", contato: "" },
    areaAtuacao: [],
    disponibilidade: {},
    certificados: [newCertificateItem()], // Inicia com um item para facilitar
    fotoFile: null,
    fotoPreviewUrl: null,
    // Documentos obrigatórios de identidade/antecedentes
    rgFrenteFile: null,
    rgVersoFile: null,
    antecedentesFile: null,
    comprovanteResidenciaFile: null,
  });

  const tagsDisponiveis = [
    "Recém-nascido",
    "Bebês (1-2 anos)",
    "Pré-escolar",
    "Idade Escolar",
    "Autismo",
    "Necessidades Especiais",
  ];

  const idiomasOpcoes = [
    "Português",
    "Inglês",
    "Espanhol",
    "Francês",
    "Libras",
    "Outro", // Changed to "Outro" for consistency
  ];

  const servicosBasicos = BABYSITTER_BASIC_SERVICES;
  const servicosOpcoes = BABYSITTER_EXTRA_SERVICES;

  const niveisOpcoes = [
    "Iniciante (menos de 2 anos)",
    "Intermediário (2-5 anos)",
    "Experiente (5+ anos)",
  ];

  const br = "\n";

  const steps = [
    {
      field: "nome",
      pergunta: `Oi! 😊 Vamos começar?${br}Qual é o seu nome?`,
      type: "text",
      placeholder: "Seu primeiro nome",
    },
    {
      field: "sobrenome",
      pergunta: `Muito prazer, ${dados.nome}! E o seu sobrenome?`,
      type: "text",
      placeholder: "Seu sobrenome completo",
    },
    {
      field: "cpf",
      pergunta: "Agora seu CPF (é seguro 🔒)",
      type: "text",
      placeholder: "Apenas números",
    },
    {
      field: "rgFrenteFile",
      pergunta: "Envie a frente do seu RG ou CNH 🪪",
      type: "doc-upload",
      accept: "image/*,application/pdf",
      maxMB: 5,
      label: "Frente do documento",
    },
    {
      field: "rgVersoFile",
      pergunta: "Agora o verso do seu RG ou CNH",
      type: "doc-upload",
      accept: "image/*,application/pdf",
      maxMB: 5,
      label: "Verso do documento",
    },
    {
      field: "antecedentesFile",
      pergunta: "Certidão de antecedentes criminais 📄",
      type: "doc-upload",
      accept: "image/*,application/pdf",
      maxMB: 10,
      label: "Certidão de antecedentes",
      hint: "Emita gratuitamente em policiavirtual.rj.gov.br ou no site da Polícia Federal.",
    },
    {
      field: "comprovanteResidenciaFile",
      pergunta: "Comprovante de residência 🏠",
      type: "doc-upload",
      accept: "image/*,application/pdf",
      maxMB: 5,
      label: "Comprovante de residência",
      hint: "Conta de luz, água, gás ou internet dos últimos 3 meses.",
    },
    {
      field: "dataNascimento",
      pergunta: "Qual sua data de nascimento?",
      type: "date",
    },
    {
      field: "cep",
      pergunta: "Qual seu CEP?",
      type: "text",
      placeholder: "00000-000",
    },
    {
      field: "cidade",
      pergunta: "Qual cidade você atende?",
      type: "text",
      placeholder: "Sua cidade",
    },
    {
      field: "numero",
      pergunta: "Qual o número da sua residência?",
      type: "text",
      placeholder: "Número ou S/N",
    },
    {
      field: "complemento",
      pergunta: "Algum complemento? (Apto, Bloco, etc.)\n\n(Opcional)",
      type: "text",
      placeholder: "Ex: Apto 101",
    },
    {
      field: "telefone",
      pergunta: "Seu telefone com DDD?",
      type: "tel",
      placeholder: "(00) 00000-0000",
    },
    {
      field: "experiencia",
      pergunta: "Conte um pouco da sua experiência como babá",
      type: "textarea",
      placeholder: "Suas vivências...",
    },
    {
      field: "nivelExperiencia",
      pergunta: "Como você avalia seu nível de experiência?",
      type: "select",
      options: niveisOpcoes,
    },
    {
      field: "raio",
      pergunta: "Até quantos KM você consegue se deslocar para trabalhar?",
      type: "number",
      placeholder: "Ex: 10",
    },
    {
      field: "aceitaEmergencial",
      pergunta: "Você aceita chamados de emergência (última hora)?",
      type: "boolean",
    },
    {
      field: "idiomas",
      pergunta: "Quais idiomas você fala?",
      type: "multi-select",
      options: idiomasOpcoes,
    },
    {
      field: "servicosBasicos",
      pergunta: "Serviços básicos incluídos no atendimento (sempre ativos):",
      type: "basic-services",
      options: servicosBasicos,
    },
    {
      field: "servicos",
      pergunta: "Quais desses serviços extras você oferece?",
      type: "multi-select",
      options: servicosOpcoes,
    },
    {
      field: "disponibilidade",
      pergunta: "Períodos disponíveis",
      type: "disponibilidade",
    },
    {
      field: "redeSocial",
      pergunta: "Qual o link da sua rede social principal?\n(Opcional)",
      type: "text",
      placeholder: "https://instagram.com/seuusuario",
    },
    {
      field: "areaAtuacao",
      pergunta: "Com quais tipos de crianças você trabalha?",
      type: "multi-select",
      options: tagsDisponiveis,
    },
    {
      field: "certificados",
      pergunta: "Você tem cursos ou qualificações?",
      type: "certificate-upload",
    },
    {
      field: "banco",
      pergunta: "Para facilitar seus pagamentos, qual o nome do seu Banco?",
      type: "text",
      placeholder: "Ex: Nubank, Itaú, Santander...",
    },
    {
      field: "agencia",
      pergunta: "Qual o número da sua agência?",
      type: "text",
      placeholder: "Ex: 0001",
    },
    {
      field: "conta",
      pergunta: "Qual o número da sua conta (com o dígito)?",
      type: "text",
      placeholder: "Ex: 12345-6",
    },
    {
      field: "pix",
      pergunta: "E qual sua chave PIX principal?",
      type: "text",
      placeholder: "CPF, Celular, E-mail ou Aleatória",
    },
    {
      field: "referencia1",
      pergunta:
        "Poderia nos passar o nome e telefone da sua 1ª referência profissional?",
      type: "contact",
      subFields: ["nome", "contato"],
    },
    {
      field: "referencia2",
      pergunta: "E os dados da sua 2ª referência?",
      type: "contact",
      subFields: ["nome", "contato"],
    },
    { field: "foto", pergunta: "Agora envie sua foto 😊", type: "file" },
    {
      field: "final",
      pergunta: "Tudo pronto! Vamos finalizar seu cadastro?",
      type: "final",
    },
  ];

  const currentStep = steps[step];

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
  };

  const fetchExistingData = useCallback(async (userId) => {
    setLoading(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from("usuarios")
        .select("*")
        .eq("id", userId)
        .single();

      if (userError && userError.code !== "PGRST116") throw userError;

      const { data: babaData, error: babaError } = await supabase
        .from("usuarios_babas")
        .select("*")
        .eq("usuario_id", userId)
        .single();

      if (babaError && babaError.code !== "PGRST116") throw babaError;

      if (userData || babaData) {
        const combined = { ...userData, ...babaData };

        setDados((prev) => ({
          ...prev,
          nome: combined.nome === "Novo" ? "" : combined.nome || "",
          sobrenome:
            combined.sobrenome === "Usuário" ? "" : combined.sobrenome || "",
          dataNascimento: combined.data_nascimento || "",
          cpf: maskCPF(combined.cpf || ""),
          cep: maskCEP(combined.cep || ""),
          cidade: combined.cidade || "",
          endereco: combined.endereco || "",
          bairro: combined.bairro || "",
          estado: combined.estado || "",
          numero: combined.numero || "",
          complemento: combined.complemento || "",
          telefone: maskPhone(combined.telefone || ""),
          experiencia: combined.experiencia_text || "",
          qualificacoes: combined.qualificacoes_text || "",
          idiomas: combined.idiomas_falados || [],
          servicos: sanitizeExtraServices(combined.servicos_extras),
          banco: combined.banco || "",
          agencia: combined.agencia || "",
          conta: combined.conta || "",
          pix: combined.pix || "",
          redeSocial: combined.rede_social || "",
          nivelExperiencia: combined.nivel_experiencia || "",
          aceitaEmergencial: combined.aceita_emergencial || false,
          raio: combined.raio_atendimento_km || 10,
          referencia1: {
            nome: combined.referencias_dados?.[0]?.nome || "",
            contato: combined.referencias_dados?.[0]?.contato || "",
          },
          referencia2: {
            nome: combined.referencias_dados?.[1]?.nome || "",
            contato: combined.referencias_dados?.[1]?.contato || "",
          },
          areaAtuacao: combined.area_atuacao || [],
          disponibilidade: combined.disponibilidade || {},
          certificados:
            (combined.certificados_docs || []).length > 0
              ? combined.certificados_docs.map((c) => ({
                  id: uuidv4(),
                  nome: c.nome || "",
                  previewUrl: c.url || null,
                  file: null,
                  fileError: null,
                }))
              : [newCertificateItem()],
          fotoPreviewUrl: combined.foto_url || null,
        }));
      }
    } catch (err) {
      console.error("Erro ao carregar dados existentes:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminEditingUserId) {
      fetchExistingData(adminEditingUserId);
      return;
    }

    // Tenta obter sessão imediata (caso já esteja no localStorage)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) fetchExistingData(session.user.id);
    });

    // Fallback para race condition do OAuth: a sessão pode chegar
    // após o mount quando vinda de redirect do Google.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session) {
        fetchExistingData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [adminEditingUserId, fetchExistingData]);

  const closeNotification = () => {
    setNotification({ ...notification, show: false });
  };

  const handlePickContact = async (field) => {
    if (!("contacts" in navigator && "select" in navigator.contacts)) {
      showNotification(
        "A seleção de contatos não é suportada neste navegador.",
        "error",
      );
      return;
    }
    try {
      const contacts = await navigator.contacts.select(["name", "tel"], {
        multiple: false,
      });
      if (contacts.length) {
        const contact = contacts[0];
        const nome = contact.name ? contact.name[0] : "";
        const tel = contact.tel ? contact.tel[0].replace(/\D/g, "") : "";
        const maskedTel = maskPhone(tel);

        setDados((prev) => ({
          ...prev,
          [field]: {
            nome: nome || prev[field].nome,
            contato: maskedTel || prev[field].contato,
          },
        }));
      }
    } catch (e) {
      console.error("Erro ao selecionar contato:", e);
    }
  };

  const toggleDisponibilidade = (dia, periodo) => {
    setDados((prev) => ({
      ...prev,
      disponibilidade: {
        ...prev.disponibilidade,
        [dia]: {
          ...(prev.disponibilidade[dia] || {}),
          [periodo]: !prev.disponibilidade[dia]?.[periodo],
        },
      },
    }));
  };

  const toggleDiaTodo = (dia) => {
    setDados((prev) => {
      const estadoAtualDia = prev.disponibilidade[dia] || {};
      const todosAtivos = periodosTrabalho.every((p) => estadoAtualDia[p.key]);
      const novoEstadoDia = {};
      periodosTrabalho.forEach((p) => {
        novoEstadoDia[p.key] = !todosAtivos;
      });
      return {
        ...prev,
        disponibilidade: { ...prev.disponibilidade, [dia]: novoEstadoDia },
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "cpf") {
      newValue = maskCPF(value);
    } else if (name === "cep") {
      newValue = maskCEP(value);
    } else if (
      name === "telefone" ||
      name === "referencia1_contato" ||
      name === "referencia2_contato"
    ) {
      newValue = maskPhone(value);
    } else if (currentStep.type === "number") {
      newValue = value === "" ? "" : Number(value);
    }

    setStepError("");
    setStepWarn("");
    setDados((prev) => ({ ...prev, [name]: newValue }));
  };

  // 🔹 BUSCA AUTOMÁTICA DE CEP (apenas Rio de Janeiro)
  useEffect(() => {
    const fetchCEP = async () => {
      const cleanedCep = dados.cep.replace(/\D/g, "");
      if (cleanedCep.length === 8) {
        setIsCepLoading(true);
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${cleanedCep}/json/`,
          );
          const data = await response.json();

          if (data.erro) {
            setStepError("CEP não encontrado. Verifique e tente novamente.");
          } else if (data.uf !== "RJ") {
            setStepError(
              `Este CEP pertence a ${data.localidade || "outro estado"} (${data.uf || "UF desconhecida"}). O serviço Tesourinhos atende apenas o Rio de Janeiro.`,
            );
            setDados((prev) => ({
              ...prev,
              cep: "",
              endereco: "",
              bairro: "",
              cidade: "",
              estado: "",
            }));
          } else {
            setStepError("");
            setDados((prev) => ({
              ...prev,
              cidade: data.localidade || "",
              endereco: data.logradouro || "",
              bairro: data.bairro || "",
              estado: data.uf || "",
            }));
          }
        } catch (err) {
          console.error("Erro ao buscar CEP:", err);
        } finally {
          setIsCepLoading(false);
        }
      }
    };
    fetchCEP();
  }, [dados.cep]);

  // Cleanup de Object URLs ao desmontar o componente
  useEffect(() => {
    const fotoPreviewUrl = dados.fotoPreviewUrl;
    const certificatePreviewUrls = (dados.certificados || [])
      .map((cert) => cert.previewUrl)
      .filter(Boolean);

    return () => {
      if (fotoPreviewUrl) URL.revokeObjectURL(fotoPreviewUrl);
      certificatePreviewUrls.forEach((previewUrl) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      });
    };
  }, [dados.certificados, dados.fotoPreviewUrl]);

  const toggleTag = (tag, field) => {
    setDados((prev) => {
      const list = prev[field] || [];
      let newList;
      let extraUpdate = {};

      if (tag === "Outro" && list.includes("Outro")) {
        // Se "Outro" já está selecionado e clicou de novo, remove "Outro" e o texto
        newList = list.filter((t) => t !== "Outro");
        extraUpdate = { [`${field}Outro`]: "" }; // Limpa o campo de texto no mesmo estado
      } else if (tag === "Outro" && !list.includes("Outro")) {
        // Se "Outro" não está selecionado e clicou, adiciona
        newList = [...list, "Outro"];
      } else {
        // Para outras tags, comportamento normal
        newList = list.includes(tag)
          ? list.filter((t) => t !== tag)
          : [...list, tag];
      }
      return { ...prev, ...extraUpdate, [field]: newList };
    });
  };

  // Gerenciamento de Certificados
  const handleAddCertificateField = () => {
    if (dados.certificados.length < 5) {
      setDados((prev) => ({
        ...prev,
        certificados: [...prev.certificados, newCertificateItem()],
      }));
    } else {
      showNotification(
        "Você pode adicionar no máximo 5 certificados.",
        "warning",
      );
    }
  };

  const handleRemoveCertificateField = (idToRemove) => {
    if (window.confirm("Tem certeza que deseja remover este certificado?")) {
      setDados((prev) => ({
        ...prev,
        certificados: prev.certificados.filter(
          (cert) => cert.id !== idToRemove,
        ),
      }));
    }
  };

  const handleCertificateNameChange = (id, name) => {
    setDados((prev) => ({
      ...prev,
      certificados: prev.certificados.map((cert) =>
        cert.id === id ? { ...cert, nome: name } : cert,
      ),
    }));
  };

  const handleCertificateFileChange = async (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    let error = null;
    const isPdf = file.type === "application/pdf";
    const isImg = file.type.startsWith("image/");

    if (!isPdf && !isImg) {
      error = "Apenas PDF ou Imagens são permitidos.";
    } else if (isPdf && file.size > 450 * 1024) {
      error = "O PDF deve ter no máximo 450KB.";
    }

    setDados((prev) => ({
      ...prev,
      certificados: prev.certificados.map((cert) => {
        if (cert.id !== id) return cert;
        if (cert.previewUrl) URL.revokeObjectURL(cert.previewUrl);
        return {
          ...cert,
          file: error ? null : file,
          fileError: error,
          previewUrl: error ? null : URL.createObjectURL(file),
        };
      }),
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // Pré-processa para garantir que HEIC ou imagens grandes sejam lidas
      const processedFile = await compressImageToWebP(file, {
        maxDimension: 2000,
        maxSizeBytes: 5 * 1024 * 1024, // Limite alto apenas para leitura
      });
      const url = URL.createObjectURL(processedFile);
      setTempImgSrc(url);
      setCropperOpen(true);
      e.target.value = null; // Reset input para permitir selecionar a mesma imagem se necessário
    } catch (err) {
      console.error("Erro ao processar imagem:", err);
      showNotification(
        "Não foi possível processar o arquivo. Tente outra imagem.",
        "error",
      );
      e.target.value = null;
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      // Aplica a compressão final e limite de tamanho após o corte
      const finalFile = await compressImageToWebP(croppedBlob, {
        maxDimension: 800,
        maxSizeBytes: 150 * 1024,
      });

      if (dados.fotoPreviewUrl) {
        URL.revokeObjectURL(dados.fotoPreviewUrl);
      }

      setDados((prev) => ({
        ...prev,
        fotoFile: finalFile,
        fotoPreviewUrl: URL.createObjectURL(finalFile),
      }));
      setCropperOpen(false);
      setTempImgSrc(null);
    } catch (err) {
      console.error("Erro ao processar corte:", err);
      showNotification("Erro ao processar a imagem cortada.", "error");
    }
  };

  const handleNext = async () => {
    setStepError("");
    setStepWarn("");

    // ── Campos obrigatórios simples ──────────────────────────────────────────
    const optionalFields = ["redeSocial", "complemento"];
    const skipEmptyCheck = [
      "multi-select", "basic-services", "boolean",
      "contact", "certificate-upload", "select", "doc-upload",
    ];
    if (
      currentStep.field !== "final" &&
      currentStep.type !== "file" &&
      !optionalFields.includes(currentStep.field) &&
      !skipEmptyCheck.includes(currentStep.type) &&
      !dados[currentStep.field]
    ) {
      setStepError("Preencha este campo antes de continuar.");
      return;
    }

    // ── Validação doc-upload ──────────────────────────────────────────────────
    if (currentStep.type === "doc-upload") {
      const file = dados[currentStep.field];
      if (!file) {
        setStepError(`Por favor, envie ${currentStep.label || "o documento"} antes de continuar.`);
        return;
      }
      const maxBytes = (currentStep.maxMB || 10) * 1024 * 1024;
      if (file.size > maxBytes) {
        setStepError(`Arquivo muito grande. O limite é ${currentStep.maxMB || 10} MB.`);
        return;
      }
    }

    // ── Nome ─────────────────────────────────────────────────────────────────
    if (currentStep.field === "nome" && dados.nome) {
      if (!validateName(dados.nome)) {
        setStepError("Nome inválido. Use apenas letras e espaços.");
        return;
      }
    }
    if (currentStep.field === "sobrenome" && dados.sobrenome) {
      if (!validateName(dados.sobrenome)) {
        setStepError("Sobrenome inválido. Use apenas letras e espaços.");
        return;
      }
    }

    // ── CPF ──────────────────────────────────────────────────────────────────
    if (currentStep.field === "cpf") {
      if (!validateCPF(dados.cpf)) {
        setStepError("CPF inválido. Verifique os números informados.");
        return;
      }
      setLoading(true);
      try {
        const cleanCPF = dados.cpf.replace(/\D/g, "");
        const { data: exists, error: rpcError } = await supabase.rpc(
          "rpc_check_cpf_exists",
          { p_cpf: cleanCPF },
        );
        if (rpcError) throw rpcError;
        if (exists) {
          setNotification({
            show: true,
            message:
              "Já existe uma conta vinculada a este CPF. Vamos te redirecionar para o login.",
            type: "info",
            onAfterClose: () => navigate("/login"),
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("Erro ao verificar CPF:", err);
      } finally {
        setLoading(false);
      }
    }

    // ── Data de Nascimento ───────────────────────────────────────────────────
    if (currentStep.field === "dataNascimento" && dados.dataNascimento) {
      if (!validateDate(dados.dataNascimento)) {
        setStepError("Data inválida. Informe uma data real posterior a 1900.");
        return;
      }
      if (!validateMinAge(dados.dataNascimento, 18, 100)) {
        setStepError("Você precisa ter entre 18 e 100 anos para se cadastrar como babá.");
        return;
      }
    }

    // ── Telefone ─────────────────────────────────────────────────────────────
    if (currentStep.field === "telefone" && dados.telefone) {
      const phoneResult = validatePhone(dados.telefone);
      if (!phoneResult.valid) {
        setStepError(phoneResult.msg);
        return;
      }
      if (phoneResult.warn) {
        setStepWarn(phoneResult.warn);
      }
    }

    // ── CEP ──────────────────────────────────────────────────────────────────
    if (currentStep.field === "cep" && dados.cep) {
      if (!validateCEP(dados.cep)) {
        setStepError("CEP incompleto. Informe todos os 8 dígitos.");
        return;
      }
    }

    // ── Rede Social (opcional, mas valida formato se preenchida) ─────────────
    if (currentStep.field === "redeSocial" && dados.redeSocial) {
      if (!validateURL(dados.redeSocial)) {
        setStepError("Link inválido. Use o formato: https://instagram.com/seuusuario");
        return;
      }
    }

    // ── Referências ───────────────────────────────────────────────────────────
    if (currentStep.type === "contact") {
      const ref = dados[currentStep.field];
      if (!ref.nome || !ref.contato) {
        setStepError("Preencha o nome e o telefone da referência.");
        return;
      }
      const refPhone = validatePhone(ref.contato);
      if (!refPhone.valid) {
        setStepError(`Telefone da referência: ${refPhone.msg}`);
        return;
      }
    }

    // ── Multi-select obrigatório ──────────────────────────────────────────────
    if (
      currentStep.type === "multi-select" &&
      dados[currentStep.field].length === 0
    ) {
      setStepError("Selecione pelo menos uma opção.");
      return;
    }

    // ── Disponibilidade ───────────────────────────────────────────────────────
    if (currentStep.type === "disponibilidade") {
      const temAlgum = Object.values(dados.disponibilidade).some((dia) =>
        Object.values(dia).some((valor) => valor === true),
      );
      if (!temAlgum) {
        setStepError("Selecione pelo menos um dia e período disponível.");
        return;
      }
    }

    // ── Certificados ─────────────────────────────────────────────────────────
    if (
      currentStep.type === "certificate-upload" &&
      dados.certificados.some(
        (cert) => cert.nome.trim().length > 0 && !cert.file && !cert.previewUrl,
      )
    ) {
      setStepError("Adicione o arquivo para todos os certificados com nome preenchido.");
      return;
    }

    // ── Foto obrigatória ──────────────────────────────────────────────────────
    if (currentStep.type === "file" && !dados.fotoFile) {
      setStepError("Por favor, selecione uma foto para continuar.");
      return;
    }

    if (currentStep.field === "final") {
      handleSubmit();
      return;
    }
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    let prevStep = step - 1;
    while (
      prevStep >= 0 &&
      steps[prevStep].condition &&
      !steps[prevStep].condition()
    ) {
      prevStep--;
    }
    setStep(prevStep);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let targetId = adminEditingUserId;
      let authUser = null;
      if (!targetId) {
        // 1ª tentativa: sessão local (rápida, sem rede)
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        authUser = currentSession?.user ?? null;

        // 2ª tentativa: refresh do token (resolve race condition pós-OAuth)
        if (!authUser) {
          const { data: refreshData } = await supabase.auth.refreshSession();
          authUser = refreshData?.session?.user ?? null;
        }

        // 3ª tentativa: verificação direta no servidor
        if (!authUser) {
          const { data: { user: serverUser } } = await supabase.auth.getUser();
          authUser = serverUser ?? null;
        }

        if (!authUser) {
          showNotification(
            "Sessão expirada. Você será redirecionada para o login.",
            "error",
          );
          setTimeout(() => navigate("/login"), 2500);
          return;
        }
        targetId = authUser.id;

        const nomeFromForm = (dados.nome || "").trim();
        const sobrenomeFromForm = (dados.sobrenome || "").trim();
        const fullName = (authUser.user_metadata?.full_name || authUser.user_metadata?.name || "").trim();
        const [firstName = "", ...restName] = fullName.split(" ").filter(Boolean);
        const nomeBase = nomeFromForm || firstName || "Usuário";
        const sobrenomeBase = sobrenomeFromForm || restName.join(" ") || "Google";

        // Garante registro base em `usuarios` antes da RPC gravar em `usuarios_babas`.
        const { error: ensureUserError } = await supabase.from("usuarios").upsert(
          {
            id: targetId,
            email: authUser.email?.trim().toLowerCase() || null,
            tipo: "baba",
            nome: nomeBase,
            sobrenome: sobrenomeBase,
            doc_status: "incompleto",
          },
          { onConflict: "id" },
        );

        if (ensureUserError) {
          throw ensureUserError;
        }
      }

      let fotoPath = null;
      if (dados.fotoFile) {
        console.log(
          "DEBUG [Babá]: Iniciando upload da imagem...",
          dados.fotoFile,
        );
        // Certifique-se de que o bucket "fotos-perfil" existe no Supabase.
        // Se o erro "Bucket not found" persistir mencionando "uploads",
        // verifique a implementação interna de uploadToStorage.js
        const { path, error: uploadError } = await uploadToStorage(
          dados.fotoFile,
          "baba", // Use o apelido "baba" para mapear para o bucket "fotos-perfil"
          { userId: targetId },
        );

        if (uploadError) throw new Error(uploadError);

        fotoPath = path;
        console.log("DEBUG [Babá]: Upload concluído. Path:", fotoPath);
      }

      // Upload dos documentos de identidade/antecedentes/residência
      const uploadDocField = async (file, subfolder) => {
        if (!file) return null;
        const { path, error } = await uploadToStorage(file, "certificados", {
          userId: targetId,
          subFolder: subfolder,
        });
        if (error) throw new Error(error);
        return path;
      };

      const [rgFrentePath, rgVersoPath, antecedentesPath, comprovanteResidenciaPath] =
        await Promise.all([
          uploadDocField(dados.rgFrenteFile, "rg-frente"),
          uploadDocField(dados.rgVersoFile, "rg-verso"),
          uploadDocField(dados.antecedentesFile, "antecedentes"),
          uploadDocField(dados.comprovanteResidenciaFile, "comprovante-residencia"),
        ]);

      // 1. Processamento e Upload de Certificados
      const uploadedCertificados = await Promise.all(
        dados.certificados.map(async (cert) => {
          if (cert.file) {
            const { path: certPath, error: certUploadError } =
              await uploadToStorage(cert.file, "certificados", {
                userId: targetId,
              });
            if (certUploadError) throw new Error(certUploadError);
            return { nome: cert.nome, url: certPath };
          }
          // Se já tem previewUrl e não tem arquivo novo, mantém o link antigo
          if (cert.previewUrl && !cert.file) {
            return { nome: cert.nome, url: cert.previewUrl };
          }
          return null;
        }),
      );
      const certificadosDocs = uploadedCertificados.filter((c) => c !== null);

      // Mesclar idiomasOutro no array de idiomas para salvar no banco
      let idiomasFalados = [...dados.idiomas];
      if (idiomasFalados.includes("Outro")) {
        // Remove a label genérica "Outro"
        idiomasFalados = idiomasFalados.filter((i) => i !== "Outro");
        // Adiciona o valor customizado se existir e não for vazio
        if (dados.idiomasOutro && dados.idiomasOutro.trim()) {
          idiomasFalados.push(dados.idiomasOutro.trim());
        }
      }

      const rpcPayload = {
        p_user_id: targetId,
        p_basic_data: {
          nome: dados.nome,
          sobrenome: dados.sobrenome,
          cidade: dados.cidade,
          estado: dados.estado,
          cep: dados.cep.replace(/\D/g, ""),
          endereco: dados.endereco,
          bairro: dados.bairro,
          numero: dados.numero,
          complemento: dados.complemento,
          telefone: dados.telefone.replace(/\D/g, ""),
          banco: dados.banco,
          agencia: dados.agencia,
          conta: dados.conta,
          pix: dados.pix,
          foto_url: fotoPath,
        },
        p_extra_data: {
          experiencia_text: dados.experiencia,
          qualificacoes_text: dados.qualificacoes,
          nivel_experiencia: dados.nivelExperiencia,
          aceita_emergencial: dados.aceitaEmergencial,
          // Number("") é NaN; o || garante que um campo de raio limpo não vire
          // "" e quebre o cast ::integer na rpc_save_user_profile.
          raio_atendimento_km: Number(dados.raio) || 10,
          idiomas_falados: idiomasFalados,
          servicos_extras: sanitizeExtraServices(dados.servicos),
          referencias_dados: [dados.referencia1, dados.referencia2],
          rede_social: dados.redeSocial,
          certificados_docs: certificadosDocs, // Salva os certificados
          area_atuacao: dados.areaAtuacao,
          data_nascimento: dados.dataNascimento || null,
          cpf: dados.cpf,
          disponibilidade: dados.disponibilidade,
          // Caminhos dos documentos de identidade/antecedentes/residência
          rg_frente_path: rgFrentePath,
          rg_verso_path: rgVersoPath,
          antecedentes_path: antecedentesPath,
          comprovante_residencia_path: comprovanteResidenciaPath,
        },
        p_role: "baba",
      };

      console.log(
        "DEBUG [Babá]: Enviando dados para RPC (rpc_save_user_profile):",
        rpcPayload,
      );

      if (authUser?.email) {
        console.log(
          "DEBUG [Babá]: Usuário base garantido em usuarios para:",
          authUser.email,
          targetId,
        );
      }

      const { error } = await supabase.rpc("rpc_save_user_profile", rpcPayload);

      if (error) throw error;

      // Cadastro concluído (com documentos) → vai para análise do admin.
      // Só promove de 'incompleto'; nunca rebaixa 'enviado'/'aprovado'.
      try {
        await supabase.rpc("rpc_mark_docs_submitted", { p_user_id: targetId });
      } catch (markError) {
        console.warn("Falha ao promover doc_status para 'enviado':", markError);
      }

      try {
        await supabase.channel("usuarios:refresh").send({
          type: "broadcast",
          event: "usuarios_changed",
          payload: { userId: targetId, role: "baba" },
        });
      } catch (broadcastError) {
        console.warn("Falha ao emitir broadcast usuarios_changed:", broadcastError);
      }

      showNotification("Cadastro finalizado com sucesso!", "success");
      // Only navigate if not admin editing
      if (!adminEditingUserId) {
        setTimeout(() => navigate("/dashboard-baba"), 2000);
      }
    } catch (err) {
      showNotification("Erro ao salvar cadastro: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Tailwind class helpers
  const inputCls = "w-full rounded-xl border border-warm-200 bg-white px-4 py-3 text-sm text-warm-800 placeholder:text-warm-400 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:border-rose-400 transition";
  const tagCls = (active) =>
    `rounded-xl border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
      active
        ? "border-rose-400 bg-rose-400/10 text-rose-400"
        : "border-warm-200 bg-white text-warm-700 hover:border-rose-400"
    }`;
  const circleCls = (ativo) =>
    `flex h-7 w-7 items-center justify-center rounded-full border transition cursor-pointer ${
      ativo
        ? "border-rose-400 bg-rose-400 text-white"
        : "border-warm-200 bg-white text-transparent hover:border-rose-400"
    }`;

  const renderInput = () => {
    const errorBlock = stepError ? (
      <p className="mt-2 text-xs font-medium text-red-500">{stepError}</p>
    ) : stepWarn ? (
      <p className="mt-2 text-xs font-medium text-amber-500">{stepWarn}</p>
    ) : null;

    switch (currentStep.type) {
      case "text":
      case "tel":
      case "date":
      case "number":
        return (
          <>
            <input
              key={currentStep.field}
              id="baba-reg-field-input"
              type={currentStep.type === "tel" ? "text" : currentStep.type}
              name={currentStep.field}
              value={dados[currentStep.field] || ""}
              onChange={handleChange}
              placeholder={currentStep.placeholder}
              autoFocus
              min={currentStep.type === "number" ? 0 : undefined}
              onKeyDown={(e) => e.key === "Enter" && handleNext()}
              className={`${inputCls}${stepError ? " border-red-400 ring-1 ring-red-400/40" : ""}`}
            />
            {errorBlock}
          </>
        );
      case "textarea":
        return (
          <>
            <textarea
              key={currentStep.field}
              id="baba-reg-field-textarea"
              name={currentStep.field}
              value={dados[currentStep.field] || ""}
              onChange={handleChange}
              placeholder={currentStep.placeholder}
              rows={4}
              maxLength={
                currentStep.field === "experiencia" ||
                currentStep.field === "qualificacoes"
                  ? 1000
                  : undefined
              }
              autoFocus
              className={inputCls}
            />
            {errorBlock}
          </>
        );
      case "select":
        return (
          <div id="baba-reg-select-options" className="flex flex-wrap gap-2">
            {currentStep.options.map((opt) => (
              <button
                key={opt}
                id={`baba-reg-select-btn-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                type="button"
                className={tagCls(dados[currentStep.field] === opt)}
                onClick={() =>
                  setDados((prev) => ({ ...prev, [currentStep.field]: opt }))
                }
                disabled={loading}
              >
                {opt}
              </button>
            ))}
          </div>
        );
      case "boolean":
        return (
          <div id="baba-reg-bool-options" className="flex gap-3">
            <button
              id="baba-reg-bool-yes"
              type="button"
              className={tagCls(dados[currentStep.field] === true)}
              onClick={() =>
                setDados((prev) => ({ ...prev, [currentStep.field]: true }))
              }
              disabled={loading}
            >
              Sim
            </button>
            <button
              id="baba-reg-bool-no"
              type="button"
              className={tagCls(dados[currentStep.field] === false)}
              onClick={() =>
                setDados((prev) => ({ ...prev, [currentStep.field]: false }))
              }
              disabled={loading}
            >
              Não
            </button>
          </div>
        );
      case "multi-select":
        return (
          <div id="baba-reg-tags-wrapper" className="flex flex-wrap gap-2">
            {currentStep.options.map((opt) => (
              <button
                key={opt}
                id={`baba-reg-tag-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                type="button"
                className={tagCls(dados[currentStep.field].includes(opt))}
                onClick={() => toggleTag(opt, currentStep.field)}
                disabled={loading}
              >
                {opt}
                {currentStep.field === "idiomas" &&
                  opt === "Outro" &&
                  dados.idiomas.includes("Outro") && (
                    <input
                      id="baba-reg-input-lang-other"
                      type="text"
                      placeholder="Qual idioma?"
                      value={dados.idiomasOutro || ""}
                      onChange={(e) =>
                        setDados((prev) => ({
                          ...prev,
                          idiomasOutro: e.target.value,
                        }))
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="ml-2 rounded border border-warm-200 px-2 py-1 text-xs"
                    />
                  )}
              </button>
            ))}
          </div>
        );
      case "basic-services":
        return (
          <div id="baba-reg-basic-services" className="space-y-3">
            <p className="text-sm text-warm-600">
              Estes serviços fazem parte do cuidado base e já estão incluídos no atendimento.
            </p>
            <div className="flex flex-wrap gap-2">
              {currentStep.options.map((service) => (
                <span
                  key={service}
                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>
        );
      case "disponibilidade":
        return (
          <div id="baba-reg-disp-table-container" className="overflow-x-auto -mx-1">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="w-10 pb-2" />
                  {periodosTrabalho.map((p) => (
                    <th key={p.key} className="pb-2 text-center font-medium text-warm-500">{p.label}</th>
                  ))}
                  <th className="pb-2 text-center font-medium text-warm-500">Todos</th>
                </tr>
              </thead>
              <tbody>
                {diasSemana.map((dia) => (
                  <tr key={dia.key}>
                    <td className="py-1 pr-2 font-medium text-warm-600">{dia.label}</td>
                    {periodosTrabalho.map((periodo) => {
                      const ativo = dados.disponibilidade[dia.key]?.[periodo.key];
                      return (
                        <td key={periodo.key} className="py-1 text-center">
                          <button
                            id={`baba-reg-disp-${dia.key}-${periodo.key}`}
                            type="button"
                            onClick={() => toggleDisponibilidade(dia.key, periodo.key)}
                            className={`mx-auto ${circleCls(ativo)}`}
                          >
                            <Check size={13} strokeWidth={3} />
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-1 text-center">
                      <button
                        id={`baba-reg-disp-${dia.key}-all`}
                        type="button"
                        onClick={() => toggleDiaTodo(dia.key)}
                        className={`mx-auto ${circleCls(periodosTrabalho.every((p) => dados.disponibilidade[dia.key]?.[p.key]))}`}
                      >
                        <Check size={13} strokeWidth={3} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "certificate-upload":
        return (
          <div id="baba-reg-certs-container" className="space-y-3">
            {dados.certificados.map((cert, index) => (
              <div key={cert.id} id={`baba-reg-cert-item-${index}`} className="space-y-2 rounded-xl border border-warm-100 bg-warm-50 p-3">
                <input
                  id={`baba-reg-cert-input-name-${index}`}
                  type="text"
                  placeholder={`Nome do Certificado ${index + 1}`}
                  value={cert.nome ?? ""}
                  onChange={(e) => handleCertificateNameChange(cert.id, e.target.value)}
                  disabled={loading}
                  className={inputCls}
                />
                <input
                  id={`baba-reg-cert-file-input-${index}`}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleCertificateFileChange(cert.id, e)}
                  disabled={loading}
                  className="text-xs text-warm-600"
                />
                {cert.previewUrl && (
                  <a href={cert.previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-rose-400 hover:underline block">
                    Ver arquivo
                  </a>
                )}
                {cert.fileError && (
                  <p className="text-xs text-red-500">{cert.fileError}</p>
                )}
                {dados.certificados.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveCertificateField(cert.id)}
                    disabled={loading}
                    className="text-xs text-red-400 hover:text-red-600 transition"
                  >
                    Remover
                  </button>
                )}
              </div>
            ))}
            {dados.certificados.length < 5 && (
              <button
                id="baba-reg-btn-add-cert"
                type="button"
                onClick={handleAddCertificateField}
                disabled={loading}
                className="rounded-full border border-rose-400 px-4 py-1.5 text-sm text-rose-400 hover:bg-rose-400/10 transition-colors"
              >
                + Adicionar certificado
              </button>
            )}
          </div>
        );
      case "contact":
        return (
          <div id="baba-reg-contact-wrapper" className="space-y-2">
            <div className="flex gap-2">
              <input
                id="baba-reg-contact-name"
                key={currentStep.field + "_nome"}
                autoFocus
                type="text"
                placeholder="Nome da referência"
                value={dados[currentStep.field]?.nome || ""}
                onChange={(e) =>
                  setDados((prev) => ({
                    ...prev,
                    [currentStep.field]: {
                      ...prev[currentStep.field],
                      nome: e.target.value,
                    },
                  }))
                }
                className={`${inputCls} flex-1`}
              />
              <button
                id="baba-reg-btn-pick-contact"
                type="button"
                onClick={() => handlePickContact(currentStep.field)}
                title="Pegar da agenda"
                className="flex h-11.5 w-11.5 shrink-0 items-center justify-center rounded-xl border border-warm-200 bg-warm-50 text-warm-500 hover:bg-warm-100 transition-colors"
              >
                <Contact size={18} />
              </button>
            </div>
            <input
              id="baba-reg-contact-tel"
              type="text"
              placeholder="Telefone/WhatsApp"
              value={dados[currentStep.field]?.contato || ""}
              onChange={(e) =>
                setDados((prev) => ({
                  ...prev,
                  [currentStep.field]: {
                    ...prev[currentStep.field],
                    contato: maskPhone(e.target.value),
                  },
                }))
              }
              className={inputCls}
            />
          </div>
        );
      case "doc-upload": {
        const currentFile = dados[currentStep.field];
        const fieldId = `baba-reg-doc-${currentStep.field}`;
        return (
          <div id={`${fieldId}-wrapper`} className="space-y-3">
            {currentStep.hint && (
              <p className="text-xs text-warm-500 rounded-xl border border-warm-100 bg-warm-50 px-3 py-2">
                💡 {currentStep.hint}
              </p>
            )}
            <label
              htmlFor={fieldId}
              className={[
                "flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed p-6 text-center transition-colors",
                currentFile
                  ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                  : "border-warm-200 bg-warm-50 text-warm-500 hover:border-rose-400 hover:text-rose-400",
              ].join(" ")}
            >
              {currentFile ? (
                <>
                  <CheckCircle size={32} />
                  <span className="text-sm font-medium">{currentFile.name}</span>
                  <span className="text-xs text-emerald-600">
                    {(currentFile.size / 1024 / 1024).toFixed(2)} MB — clique para trocar
                  </span>
                </>
              ) : (
                <>
                  <UploadCloud size={32} />
                  <span className="text-sm font-medium">{currentStep.label || "Selecionar arquivo"}</span>
                  <span className="text-xs">JPG, PNG ou PDF • máx {currentStep.maxMB || 10} MB</span>
                </>
              )}
            </label>
            <input
              id={fieldId}
              type="file"
              accept={currentStep.accept || "image/*,application/pdf"}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const maxBytes = (currentStep.maxMB || 10) * 1024 * 1024;
                if (file.size > maxBytes) {
                  setStepError(`Arquivo muito grande. O limite é ${currentStep.maxMB || 10} MB.`);
                  return;
                }
                setStepError("");
                setDados((prev) => ({ ...prev, [currentStep.field]: file }));
              }}
            />
          </div>
        );
      }
      case "file":
        return (
          <div id="baba-reg-photo-upload-wrapper" className="flex justify-center">
            <label id="baba-reg-photo-label" htmlFor="foto-input" className="cursor-pointer">
              {dados.fotoPreviewUrl ? (
                <img
                  id="baba-reg-photo-preview"
                  src={dados.fotoPreviewUrl}
                  alt="Preview"
                  className="h-36 w-36 rounded-2xl object-cover shadow-md"
                />
              ) : (
                <div className="flex h-36 w-36 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-warm-200 text-warm-400 hover:border-rose-400 hover:text-rose-400 transition-colors">
                  <Camera size={36} />
                  <span className="text-xs">Escolher foto</span>
                </div>
              )}
            </label>
            <input id="foto-input" type="file" accept="image/*" onChange={handleFileChange} hidden />
          </div>
        );
      case "final":
        return (
          <div id="baba-reg-final-step-msg" className="text-center py-4">
            <p className="text-sm text-warm-600">
              Clique no botão abaixo para confirmar seus dados e começar a receber oportunidades!
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div id="baba-reg-root" className="min-h-screen bg-linear-to-br from-warm-50 via-white to-warm-100 flex flex-col">
      {!hideHeader && (
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-warm-100 px-4 py-3 flex items-center gap-3">
          <img src="/assets/logo-tesourinhos-trans.png" alt="Tesourinhos" className="h-8 w-auto" />
          <span className="text-sm font-semibold text-warm-700">Cadastro de Babá</span>
        </header>
      )}

      <div id="baba-reg-chat-wrapper" className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {/* Progresso */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-2 rounded-full bg-warm-200 overflow-hidden">
              <div
                id="baba-reg-progress-fill"
                className="h-2 rounded-full bg-linear-to-r from-rose-400 to-slate-blue-400 transition-all duration-500"
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-warm-500 shrink-0">
              {step + 1}/{steps.length}
            </span>
          </div>

          <div id="baba-reg-chat-card" className="bg-white rounded-3xl shadow-lg px-6 py-7 sm:px-8">
            <p id="baba-reg-chat-question" className="font-heading text-xl font-bold text-warm-900 mb-6 whitespace-pre-line leading-snug">
              {isCepLoading ? "Buscando seu endereço... 🔍" : currentStep.pergunta}
            </p>
            <div id="baba-reg-chat-response">
              {renderInput()}
            </div>
          </div>

          <div id="baba-reg-navigation-actions" className="flex gap-3 mt-4">
            {step > 0 && (
              <button
                id="baba-reg-btn-back"
                onClick={handleBack}
                disabled={loading}
                className="flex items-center gap-1.5 rounded-2xl border-2 border-warm-200 px-5 py-2.5 text-sm font-semibold text-warm-600 hover:border-warm-300 transition-colors disabled:opacity-50"
              >
                <ChevronLeft size={16} /> Voltar
              </button>
            )}
            <button
              id="baba-reg-btn-next"
              onClick={handleNext}
              disabled={loading}
              className="flex-1 rounded-2xl bg-rose-400 py-2.5 text-sm font-bold text-white hover:brightness-105 transition-all disabled:opacity-60"
            >
              {loading ? "Salvando..." : step === steps.length - 1 ? "Finalizar Cadastro" : "Continuar"}
            </button>
          </div>
        </div>
      </div>
      <NotificationModal
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      {cropperOpen && tempImgSrc && (
        <ImageCropperModal
          imageSrc={tempImgSrc}
          aspect={1} // Quadrado para perfil
          onCropComplete={handleCropComplete}
          onClose={() => setCropperOpen(false)}
        />
      )}
    </div>
  );
}